import { faCheck, faGear, faUserGroup } from "@fortawesome/pro-duotone-svg-icons"
import { faChevronLeft, faChevronRight, faTimes, faPlus, faInfoCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Input, Checkbox, Typography, message, Modal, Select } from "antd"
import { useState, useEffect, useContext } from "react"
import { useAccountSetup } from "./AccountSetupContext"
import { AuthenticationContext } from "../../AuthenticationContext"
import { useCompleteAccountConfigMutation } from "../MyTenant/tenantsQueries"
import { updateCrewMember, CrewMember } from "../SystemSetup/CrewMembers/crewMemberApi"
import { updateUser, UserDto, validateUserEmail } from "../Users/usersApi"
import styled from "styled-components"
import Confetti from "react-confetti"
import './AccountSetupModal.css'
import './AccountSetupEmployeesStep.css'
import { useUsersRolesQuery } from "../Users/usersQueries"

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
  onComplete: () => void
}

const WarningBanner = styled.div`
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
`

const InfoBox = styled.div`
  position: absolute;
  top: 72px;
  left: 48px;
  right: 48px;
  z-index: 2;
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
`

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`

const InfoBoxSubtitle = styled.p`
  font-size: 13px;
  color: #595959;
  margin: 0 0 16px 0;
  line-height: 1.4;
`

const InfoBoxSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 10px 0;
`

const RoleList = styled.div`
  margin-bottom: 16px;
`

const RoleItem = styled.div`
  font-size: 13px;
  color: #1a1a1a;
  margin-bottom: 6px;
  line-height: 1.4;
`

const RoleName = styled.span`
  font-weight: 700;
`

const TipBox = styled.div`
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
`

const TipTitle = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: #1a1a1a;
  display: block;
  margin-bottom: 4px;
`

const TipText = styled.span`
  font-size: 12px;
  color: #595959;
  line-height: 1.4;
`

const EmployeeCard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`

const EmployeeCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #999;
  transition: color 0.2s;
  font-size: 16px;

  &:hover {
    color: #e85d04;
  }
`

const AddEmployeeButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: #666;
  font-size: 14px;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};
  margin-bottom: 24px;

  &:hover {
    border-color: ${props => props.disabled ? '#d9d9d9' : '#e85d04'};
    color: ${props => props.disabled ? '#666' : '#e85d04'};
  }
`

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #52c41a;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`

const PasswordInfoBox = styled.div`
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

const generatePassword = (name: string): string => {
  const firstName = name.trim().split(' ')[0]
  const capitalized = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
  return `${capitalized}${new Date().getFullYear()}!`
}

const AccountSetupEmployeesStep = ({ onBack, onExit, onComplete }: Props) => {
  const { state, dispatch } = useAccountSetup()
  const { employees } = state
  const { currentUser } = useContext(AuthenticationContext)
  const [isSaving, setIsSaving] = useState(false)
  const rolesQuery = useUsersRolesQuery()
  const roleOptions = rolesQuery.data || []
  const completeAccountConfigMutation = useCompleteAccountConfigMutation()
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleAddEmployee = () => {
    dispatch({ type: 'ADD_EMPLOYEE' })
  }

  const handleRemoveEmployee = (id: string) => {
    dispatch({ type: 'REMOVE_EMPLOYEE', payload: id })
    // Clear validation errors for this employee
    const newErrors = { ...validationErrors }
    delete newErrors[`${id}-name`]
    delete newErrors[`${id}-email`]
    delete newErrors[`${id}-roles`]
    setValidationErrors(newErrors)
  }

  const handleUpdateEmployee = (id: string, field: 'name' | 'giveLoginAccess' | 'email' | 'roles', value: string | boolean | string[]) => {
    dispatch({ type: 'UPDATE_EMPLOYEE', id, field, value })
    // Clear validation error for this field
    const errorKey = `${id}-${field}`
    if (validationErrors[errorKey]) {
      const newErrors = { ...validationErrors }
      delete newErrors[errorKey]
      setValidationErrors(newErrors)
    }
  }

  const validateEmployees = async (): Promise<boolean> => {
    const errors: Record<string, string> = {}

    for (const employee of employees) {
      // Name is required for all employees
      if (!employee.name.trim()) {
        errors[`${employee.id}-name`] = 'Name is required'
      }

      // Email and roles required if login access is checked
      if (employee.giveLoginAccess) {
        if (!employee.email.trim()) {
          errors[`${employee.id}-email`] = 'Email is required for login access'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employee.email)) {
          errors[`${employee.id}-email`] = 'Please enter a valid email address'
        } else {
          // Check if email is already in use
          try {
            const isAvailable = await validateUserEmail(employee.email)
            if (!isAvailable) {
              errors[`${employee.id}-email`] = 'This email is already in use'
            }
          } catch {
            errors[`${employee.id}-email`] = 'Unable to validate email'
          }
        }

        // Validate roles
        if (employee.roles.length === 0) {
          errors[`${employee.id}-roles`] = 'At least one role is required'
        }
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const showCompletion = async () => {
    if (currentUser?.tenant?.id) {
      await completeAccountConfigMutation.mutateAsync(currentUser.tenant.id)
    }
    setShowCompletionModal(true)
    setShowConfetti(true)

    // Stop confetti after 5 seconds
    setTimeout(() => {
      setShowConfetti(false)
    }, 5000)
  }

  const handleCompleteSetup = async () => {
    // Skip validation if no employees
    if (employees.length === 0) {
      showCompletion()
      return
    }

    // Only validate employees with names
    const employeesWithNames = employees.filter(e => e.name.trim())
    if (employeesWithNames.length === 0) {
      showCompletion()
      return
    }

    const isValid = await validateEmployees()
    if (!isValid) {
      return
    }

    setIsSaving(true)
    try {
      for (const employee of employeesWithNames) {
        let userEmail: string | null = null

        // Create user first if login access is enabled
        if (employee.giveLoginAccess && employee.email) {
          const password = generatePassword(employee.name)

          const user: UserDto = {
            id: '',
            url: '',
            displayName: employee.name.trim(),
            email: employee.email,
            avatar: '',
            prefix: '',
            color: '#1890ff',
            isActive: true,
            roles: employee.roles,
            avatarDataBase64: null,
            isSuperUser: false,
            isFreeUser: false,
            password: password
          }
          const createdUser = await updateUser(user)
          // Use the email from the API response to ensure exact match
          userEmail = createdUser.email
        }

        // Create crew member (with userEmail if login access enabled)
        const crewMember: CrewMember = {
          id: 0,
          name: employee.name.trim(),
          email: userEmail,
          userEmail: userEmail,
          isActive: true,
          timesheetNotifications: false,
          qboId: null,
          qboDisplayName: null
        }
        await updateCrewMember(crewMember)
      }

      showCompletion()
    } catch (error: any) {
      console.error('Error creating employees:', error)
      const errorMessage = error?.response?.data || 'Failed to create some employees. Please try again.'
      console.error('Server response:', errorMessage)
      message.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to create some employees. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '87.5%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faUserGroup} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            Who Works For You?
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Add your employees to enable scheduling and optional SynkedUP access. You can skip this step and add employees later.
          </Text>
        </div>

        <div className="account-setup-form" style={{ marginTop: 24 }}>
          <WarningBanner>
            <Text strong style={{ color: '#d46b08' }}>Paid Seats Notice</Text>
            <br />
            <Text style={{ fontSize: 13, color: '#8c5a00' }}>
              Giving additional users login access to SynkedUP will count towards your paid seats.
            </Text>
          </WarningBanner>

          {employees.map((employee, index) => (
            <EmployeeCard key={employee.id}>
              <EmployeeCardHeader>
                <Text strong>Employee #{index + 1}</Text>
                <RemoveButton
                  onClick={() => handleRemoveEmployee(employee.id)}
                  aria-label={`Remove employee ${index + 1}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </RemoveButton>
              </EmployeeCardHeader>

              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Name *</Text>
                <Input
                  placeholder="Employee name"
                  value={employee.name}
                  onChange={(e) => handleUpdateEmployee(employee.id, 'name', e.target.value)}
                  status={validationErrors[`${employee.id}-name`] ? 'error' : undefined}
                  disabled={isSaving}
                />
                {validationErrors[`${employee.id}-name`] && (
                  <Text type="danger" style={{ fontSize: 12 }}>{validationErrors[`${employee.id}-name`]}</Text>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={employee.giveLoginAccess}
                  onChange={(e) => handleUpdateEmployee(employee.id, 'giveLoginAccess', e.target.checked)}
                  disabled={isSaving}
                >
                  <Text strong style={{ fontSize: 13 }}>Give login access to SynkedUP</Text>
                </Checkbox>
                <Text type="secondary" style={{ display: 'block', marginLeft: 24, fontSize: 12 }}>
                  Allow this employee to log in and use the platform
                </Text>
              </div>

              {employee.giveLoginAccess && (
                <div style={{ borderLeft: '2px solid #e8e8e8', paddingLeft: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                      Email Address<span style={{ color: '#ff4d4f' }}>*</span>
                    </Text>
                    <Input
                      placeholder="employee@company.com"
                      value={employee.email}
                      onChange={(e) => handleUpdateEmployee(employee.id, 'email', e.target.value)}
                      status={validationErrors[`${employee.id}-email`] ? 'error' : undefined}
                      disabled={isSaving}
                    />
                    {validationErrors[`${employee.id}-email`] && (
                      <Text type="danger" style={{ fontSize: 12 }}>{validationErrors[`${employee.id}-email`]}</Text>
                    )}
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                      User Roles<span style={{ color: '#ff4d4f' }}>*</span>
                    </Text>
                    <Select
                      mode="multiple"
                      placeholder="Select roles..."
                      value={employee.roles}
                      onChange={(values) => handleUpdateEmployee(employee.id, 'roles', values)}
                      style={{ width: '100%' }}
                      disabled={isSaving}
                      status={validationErrors[`${employee.id}-roles`] ? 'error' : undefined}
                    >
                      {roleOptions.map(role => (
                        <Select.Option key={role.rolename} value={role.rolename}>
                          {role.rolename}
                        </Select.Option>
                      ))}
                    </Select>
                    {validationErrors[`${employee.id}-roles`] ? (
                      <Text type="danger" style={{ fontSize: 12 }}>{validationErrors[`${employee.id}-roles`]}</Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Select all roles that apply to this user
                      </Text>
                    )}
                  </div>

                  {employee.name.trim() && (
                    <PasswordInfoBox>
                      <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#1677ff', marginTop: 2 }} />
                      <div>
                        <Text strong>Account Password: </Text>
                        <Text>{generatePassword(employee.name)}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Share this password with {employee.name.trim().split(' ')[0]}
                        </Text>
                      </div>
                    </PasswordInfoBox>
                  )}
                </div>
              )}
            </EmployeeCard>
          ))}

          <AddEmployeeButton onClick={handleAddEmployee} disabled={isSaving}>
            <FontAwesomeIcon icon={faPlus} />
            Add {employees.length === 0 ? 'an' : 'Another'} Employee
          </AddEmployeeButton>

          <div className="account-setup-form-buttons">
            <Button size="large" onClick={onBack} disabled={isSaving}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCompleteSetup}
              loading={isSaving}
            >
              Complete Setup <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-7.png"
          alt="Employee management"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <InfoBox>
          <InfoBoxHeader>
            <FontAwesomeIcon icon={faGear} style={{ color: '#FF5C35', fontSize: 20 }} />
            <InfoBoxTitle>Employee Management Guide</InfoBoxTitle>
          </InfoBoxHeader>
          <InfoBoxSubtitle>
            Track all your employees and control who gets SynkedUP access. Only members with login access count toward your user limit.
          </InfoBoxSubtitle>

          <InfoBoxSectionTitle>Common Roles:</InfoBoxSectionTitle>
          <RoleList>
            <RoleItem><RoleName>Administrator:</RoleName> Full system access</RoleItem>
            <RoleItem><RoleName>Sales:</RoleName> Oversee new work coming into the business</RoleItem>
            <RoleItem><RoleName>Foreman:</RoleName> Lead crews on job sites</RoleItem>
            <RoleItem><RoleName>Scheduler:</RoleName> Schedule jobs and crews</RoleItem>
          </RoleList>

          <TipBox>
            <TipTitle>Tip:</TipTitle>
            <TipText>
              You can add employees for scheduling without giving them SynkedUP access. Only toggle "Give login access to SynkedUP" for members who need to log in.
            </TipText>
          </TipBox>
        </InfoBox>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "3 years ago I was buying tools from Harbor Freight on a credit card. Today I wrote a check for $59,000 for an excavator attachment - no stress."
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Alex Bulow</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              Inca Stone
            </Text>
          </div>
        </div>
      </div>

      {/* Completion Modal */}
      <Modal
        open={showCompletionModal}
        footer={null}
        closable={false}
        centered
        width={480}
      >
        <div style={{ textAlign: 'center', padding: '24px 16px' }}>
          <SuccessIcon>
            <FontAwesomeIcon icon={faCheck} style={{ color: 'white', fontSize: 32 }} />
          </SuccessIcon>
          <Title level={3} style={{ marginBottom: 12 }}>
            Account Setup Complete!
          </Title>
          <Text type="secondary" style={{ fontSize: 15 }}>
            Great job! Your account is configured and ready to go. Let's see what's next to get you up and running.
          </Text>
          <Button
            type="primary"
            size="large"
            style={{
              marginTop: 32,
              width: '100%',
              height: 48,
              borderRadius: 8,
              background: '#FF5C35',
              borderColor: '#FF5C35'
            }}
            onClick={onComplete}
          >
            See Next Steps
          </Button>
        </div>
      </Modal>

      {/* Confetti Effect */}
      {showConfetti && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10000
        }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={800}
            gravity={0.3}
            initialVelocityY={20}
          />
        </div>
      )}
    </div>
  )
}

export default AccountSetupEmployeesStep
